import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  List,
  TextField,
  DateField,
  TopToolbar,
  useListContext,
  ExportButton,
  FunctionField
} from 'react-admin';
import jsonExport from 'jsonexport/dist';

import FlexibleListLayout from '../FlexibleListLayout';
import YenField from '../YenField';
import downloadCSVCompat from '../../utils/downloadCSVCompat';
import { formatRFC3339DateString } from '../../utils/dateutils';

import type { BuyerDashboardType } from './BuyerList';

export interface BuyerOrderDetailListProps {
  dashboardType: BuyerDashboardType;
}

interface BuyerOrderRecord {
  id: string;
  orderId: string;
  ecSiteClientId?: string | null;
  clientId?: number;
  clientName?: string;
  partnerName?: string;
  affiliateId?: string;
  orderDate?: string | Date;
  approvedAt?: string | Date | null;
  rewardPrice?: number;
}

const MAX_EXPORT = 1000;

/**
 * 購入者管理 注文詳細画面の共通コンポーネント。
 * `/buyers/:fuid` ルートに紐づき、`<List resource={`buyers/${fuid}/orders`}>` で API を呼ぶ
 * （既存 PaymentDetailItemsList と同型の動的 resource 文字列パターン）。
 */
const BuyerOrderDetailList: React.FC<BuyerOrderDetailListProps> = ({ dashboardType }) => {
  const { fuid } = useParams<{ fuid: string }>();
  const navigate = useNavigate();

  const showClientId = dashboardType === 'admin';
  const showClientName = dashboardType === 'admin' || dashboardType === 'partner';
  const showEcSiteClientId = dashboardType !== 'partner';
  const showPartnerName = dashboardType !== 'partner';

  const handleExport = async (records: BuyerOrderRecord[]) => {
    if (!records || records.length === 0) return;
    const dataToExport = records.map((record) => {
      const row: Record<string, unknown> = {};
      row['successfee購入者ID'] = fuid;
      row['ECサイト注文ID'] = record.orderId;
      if (showEcSiteClientId) row['ECサイト顧客ID'] = record.ecSiteClientId || '';
      if (showClientId) row['ショップID'] = record.clientId ?? '';
      if (showClientName) row['ショップ名'] = record.clientName ?? '';
      if (showPartnerName) row['パートナー名'] = record.partnerName ?? '';
      row['アフィリエイトID'] = record.affiliateId ?? '';
      row['注文日時'] = record.orderDate ? formatRFC3339DateString(record.orderDate as string) : '';
      row['承認日時'] = record.approvedAt ? formatRFC3339DateString(record.approvedAt as string) : '';
      row['報酬額'] = record.rewardPrice != null ? record.rewardPrice.toLocaleString() : '';
      return row;
    });
    jsonExport(dataToExport, {}, (err: Error | null, csv: string) => {
      if (err) {
        console.error(err);
        return;
      }
      downloadCSVCompat('﻿' + csv, '注文詳細項目');
    });
  };

  const DetailActions = () => {
    const { total } = useListContext();
    return (
      <TopToolbar>
        <ExportButton label="CSVダウンロード" exporter={handleExport} disabled={total === 0} maxResults={MAX_EXPORT} />
      </TopToolbar>
    );
  };

  if (!fuid) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">URL に fuid が指定されていません。</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 2, px: 1 }}>
        <IconButton onClick={() => navigate('/buyers')} aria-label="購入者管理一覧へ戻る">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">
          購入者詳細
        </Typography>
      </Box>
      <List
        resource={`buyers/${fuid}/orders`}
        title={"購入者管理 - 詳細"}
        sort={{ field: 'orderDate', order: 'DESC' }}
        perPage={10}
        actions={<DetailActions />}
        exporter={handleExport}
        empty={
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              承認済みの注文はありません。
            </Typography>
          </Box>
        }
      >
        <FlexibleListLayout bulkActionButtons={false}>
          <FunctionField
            source="fuid"
            label="successfee購入者ID"
            render={() => fuid}
          />
          <TextField source="orderId" label="ECサイト注文ID" />
          {showEcSiteClientId && (
            <FunctionField
              source="ecSiteClientId"
              label="ECサイト顧客ID"
              render={(record: BuyerOrderRecord) =>
                record.ecSiteClientId ? (
                  record.ecSiteClientId
                ) : (
                  <Box component="div" sx={{ textAlign: 'center', width: '100%' }}>
                    -
                  </Box>
                )
              }
            />
          )}
          {showClientId && <TextField source="clientId" label="ショップID" />}
          {showClientName && <TextField source="clientName" label="ショップ名" />}
          {showPartnerName && <TextField source="partnerName" label="パートナー名" />}
          <TextField source="affiliateId" label="アフィリエイトID" />
          <DateField source="orderDate" label="注文日時" showTime />
          <DateField source="approvedAt" label="承認日時" showTime />
          <YenField source="rewardPrice" label="報酬額" />
        </FlexibleListLayout>
      </List>
    </Box>
  );
};

export default BuyerOrderDetailList;
